import { useMemo, useState } from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type { SortingState } from '@tanstack/react-table';
import type { Building } from '../types/Building';
import { buildings as allBuildingsData } from '../data/buildings';
import { useAwards } from '../hooks/useAwards';

// Filter to only show true supertalls (300m+ / 984ft+)
const buildingsData = allBuildingsData.filter(building => building.height_meters >= 300);

const columnHelper = createColumnHelper<Building>();

const createColumns = (isAwarded: (rank: number) => boolean) => [
  columnHelper.accessor('rank', {
    header: 'Rank',
    cell: info => info.getValue(),
    size: 80,
  }),
  columnHelper.accessor('name', {
    header: 'Building Name',
    cell: info => {
      const building = info.row.original;
      return (
        <div className="font-medium text-blue-900">
          {info.getValue()}
          {building.status === 'Under construction' && (
            <span className="ml-2 px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded-full">
              🚧 Under Construction
            </span>
          )}
          {isAwarded(building.rank) && (
            <span className="ml-2 px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
              🏆 Chris Morgan Architectural Achievement Award
            </span>
          )}
        </div>
      );
    },
    size: 400,
  }),
  columnHelper.accessor('height_meters', {
    header: 'Height (m)',
    cell: info => `${info.getValue()}m`,
    size: 100,
  }),
  columnHelper.accessor('height_feet', {
    header: 'Height (ft)',
    cell: info => `${info.getValue()}ft`,
    size: 100,
  }),
  columnHelper.accessor('floors', {
    header: 'Floors',
    cell: info => info.getValue(),
    size: 80,
  }),
  columnHelper.accessor('year_completed', {
    header: 'Status',
    cell: info => {
      const building = info.row.original;
      if (building.status === 'Under construction') {
        return (
          <div className="text-center">
            <span className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded-full">
              Under Construction
            </span>
            {building.expected_completion && (
              <div className="text-xs text-gray-500 mt-1">
                Est. {building.expected_completion}
              </div>
            )}
          </div>
        );
      }
      return (
        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
          {info.getValue()}
        </span>
      );
    },
    size: 120,
  }),
  columnHelper.accessor('purpose', {
    header: 'Purpose',
    cell: info => (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
        info.getValue() === 'Office' 
          ? 'bg-blue-100 text-blue-800'
          : info.getValue() === 'Residential'
          ? 'bg-green-100 text-green-800'
          : 'bg-purple-100 text-purple-800'
      }`}>
        {info.getValue()}
      </span>
    ),
    size: 120,
  }),
  columnHelper.accessor('address', {
    header: 'Address',
    cell: info => (
      <div className="text-sm text-gray-600 max-w-xs truncate">
        {info.getValue()}
      </div>
    ),
    size: 250,
  }),
];

export function SupertallsTable() {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [selectedBuilding, setSelectedBuilding] = useState<Building | null>(null);
  const { isAwarded } = useAwards();

  // Filter for supertalls (300m+ buildings)
  const supertalls = useMemo(() => {
    return buildingsData.filter(building => building.height_meters >= 300);
  }, []);

  const columns = createColumns(isAwarded);

  const table = useReactTable({
    data: supertalls,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const handleRowClick = (building: Building) => {
    setSelectedBuilding(building);
  };

  return (
    <div className="w-full">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">NYC Supertalls</h1>
        <p className="text-gray-600">
          Buildings over 300 meters tall in New York City ({supertalls.length} total)
        </p>
      </div>

      <div className="bg-white shadow-lg rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      style={{ width: header.getSize() }}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <div className="flex items-center space-x-1">
                        <span>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </span>
                        <span className="text-gray-400">
                          {{
                            asc: '↑',
                            desc: '↓',
                          }[header.column.getIsSorted() as string] ?? '↕'}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {table.getRowModel().rows.map(row => (
                <tr
                  key={row.id}
                  className="hover:bg-gray-50 cursor-pointer transition-colors duration-150"
                  onClick={() => handleRowClick(row.original)}
                >
                  {row.getVisibleCells().map(cell => (
                    <td
                      key={cell.id}
                      className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Selected Building Modal/Details */}
      {selectedBuilding && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg overflow-hidden max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Building Image */}
            {selectedBuilding.image_url && (
              <div className="relative">
                <img
                  src={selectedBuilding.image_url}
                  alt={selectedBuilding.name}
                  className="w-full h-64 object-cover"
                  loading="lazy"
                />
                {selectedBuilding.image_caption && (
                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2 text-sm">
                    {selectedBuilding.image_caption}
                  </div>
                )}
              </div>
            )}
            
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  {selectedBuilding.name}
                </h2>
                <button
                  onClick={() => setSelectedBuilding(null)}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-light"
                >
                  ×
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <span className="font-medium text-gray-700">Rank:</span>
                    <span className="ml-2 text-xl font-bold text-blue-600">#{selectedBuilding.rank}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Height:</span>
                    <div className="ml-2">
                      <span className="text-lg font-semibold">{selectedBuilding.height_meters}m</span>
                      <span className="text-gray-500 ml-1">({selectedBuilding.height_feet}ft)</span>
                    </div>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Floors:</span>
                    <span className="ml-2 text-lg">{selectedBuilding.floors}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Status:</span>
                    {selectedBuilding.status === 'Under construction' ? (
                      <div className="ml-2">
                        <span className="px-3 py-1 text-sm font-medium bg-orange-100 text-orange-800 rounded-full">
                          🚧 Under Construction
                        </span>
                        {selectedBuilding.expected_completion && (
                          <div className="text-sm text-gray-600 mt-1">
                            Expected: {selectedBuilding.expected_completion}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="ml-2 px-3 py-1 text-sm font-medium bg-green-100 text-green-800 rounded-full">
                        Completed {selectedBuilding.year_completed}
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <span className="font-medium text-gray-700">Purpose:</span>
                    <span className={`ml-2 px-2 py-1 rounded-full text-sm font-medium ${
                      selectedBuilding.purpose === 'Office' 
                        ? 'bg-blue-100 text-blue-800'
                        : selectedBuilding.purpose === 'Residential'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-purple-100 text-purple-800'
                    }`}>
                      {selectedBuilding.purpose}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Address:</span>
                    <div className="ml-2 text-sm text-gray-600">
                      {selectedBuilding.address}
                    </div>
                  </div>
                  {selectedBuilding.coordinates && (
                    <div>
                      <span className="font-medium text-gray-700">Location:</span>
                      <div className="ml-2 text-sm text-gray-600">
                        {selectedBuilding.coordinates.lat}°N, {Math.abs(selectedBuilding.coordinates.lng)}°W
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {selectedBuilding.notes && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <span className="font-medium text-gray-700">Notes:</span>
                  <p className="mt-1 text-gray-600">{selectedBuilding.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}